import { useEffect, useRef, type ReactNode } from 'react';
import type { LinkingMethod } from '../useLinkingWorkflow';
import type { LinkingOrigin } from '../linkingOrigin';
import LinkingFlowStep1 from './LinkingFlowStep1';
import LinkingFlowStep2 from './LinkingFlowStep2';
import LinkingFlowStep3 from './LinkingFlowStep3';
import LinkingCredentialsLight from './LinkingCredentialsLight';
import SATAlreadyLinkedSuccess from './SATAlreadyLinkedSuccess';
import SatRelinkScope from '../SatRelinkScope';
import { useLinkingFlowController } from './useLinkingFlowController';
import { useLinkingTracking } from './orchestration/useLinkingTracking';
import { LinkingOriginContext } from './orchestration/linkingOriginContext';
import { useLinkingExperiments } from './experiments/useLinkingExperiments';
import { useSessionReplayWindow } from '../../../shared/analytics/useSessionReplayWindow';
import LinkingRewardBanner, { BLEED } from '@/components/molecules/LinkingRewardBanner';
import useLinkingReward from '../linking-reward/useLinkingReward';

type LinkingFlowProps = {
	/**
	 * Desde qué pantalla se está vinculando. REQUERIDO a propósito: hay doce
	 * superficies que montan este flujo y ninguna dejaba rastro de cuál fue.
	 * Sin default, para que una superficie nueva no compile hasta declararlo.
	 * Ver `linkingOrigin`.
	 */
	origin: LinkingOrigin;
	isOnboarding?: boolean;
	onAlreadyLinkedContinue?: () => void;
	/**
	 * Fires when polling resolves to `completed`. When omitted, the controller
	 * falls back to `window.location.reload()` — the right behavior for the
	 * standalone /sat-linking entry, where refreshing the app is how the
	 * newly-linked SAT propagates. Inline callers (onboarding sub-steps that
	 * embed this flow) pass a handler to advance their own flow instead.
	 */
	onCompleted?: () => void;
	efirmaOnly?: boolean;
	/**
	 * Opens directly on the credentials step for this method, skipping the
	 * method picker (step 1). For surfaces that only offer one method, e.g. the
	 * consult-RFC → link screen (CIEC/RFC + password).
	 */
	startMethod?: LinkingMethod;
	/**
	 * Presentation variant. `full` (default) = method picker + heavy card with
	 * FAQs. `light` = minimal CIEC credentials screen (onboarding consult-RFC →
	 * link). Both share the same engine/orchestration.
	 */
	variant?: 'full' | 'light';
	/**
	 * Mirror the credentials step into the URL. Off for embedded surfaces.
	 */
	syncUrl?: boolean;
	/** Chrome propio del consumidor, renderizado arriba (solo `light`). */
	header?: ReactNode;
	relink?: boolean;
};

const LinkingFlowSteps = ({
	origin,
	isOnboarding = false,
	onAlreadyLinkedContinue,
	onCompleted,
	efirmaOnly = false,
	startMethod,
	variant = 'full',
	syncUrl,
	header,
	relink = false,
	directEntry = false,
}: LinkingFlowProps & { directEntry?: boolean }) => {
	const {
		step,
		setStep,
		linkingOptionSelected,
		setLinkingOptionSelected,
		showModalFailed,
		setShowModalFailed,
		isPolling,
		isLinked,
		isLoading,
		handleSelectMethod,
		handleSubmit,
		handleCancel,
	} = useLinkingFlowController({
		origin,
		onCompleted,
		initialMethod: startMethod,
		syncUrl,
		relink,
	});

	// El brazo de tratamiento no pasa por el selector, así que nadie dispararía
	// `select_method` y el indicador adelantado del embudo desaparecería justo
	// para la mitad del experimento. Se emite aquí el método con el que se abrió:
	// así "eligió método" significa "método comprometido" en los dos brazos y
	// queda comparable. Un cambio posterior desde el paso 2 emite otro, y el
	// análisis se queda con el PRIMERO por usuario.
	const { trackMethodSelected } = useLinkingTracking();
	const directEntryTracked = useRef(false);
	useEffect(() => {
		if (!directEntry || !startMethod || directEntryTracked.current) return;
		directEntryTracked.current = true;
		trackMethodSelected(startMethod);
	}, [directEntry, startMethod, trackMethodSelected]);

	// La ventana vive AQUÍ y no en SATCredentialsForm a propósito: al arrancar el
	// polling, el paso 2 se desmonta entero y monta LinkingFlowStep3. Una ventana
	// colgada del formulario se cerraría justo cuando empieza lo que interesa —
	// la espera, el fallo y qué hace el usuario con el mensaje de error. CIEC
	// falla el 85% de los intentos: ese tramo es el hallazgo, no el tecleo.
	//
	// Arranca en el paso de credenciales (o en `light`, que las pide de una) y no
	// en el selector de método: elegir CIEC o e.firma ya es intención real, y así
	// no se graba a quien solo asomó la cabeza. Cubre el RFC desde el primer
	// caracter porque la ventana abre antes de que el campo exista.
	useSessionReplayWindow(
		'sat_linking',
		!isLinked && (isPolling || variant === 'light' || step === 2),
	);

	// EL HOOK VIVE AQUÍ, EN LA RAÍZ; el banner se pinta dentro de cada paso.
	//
	// Los pasos son slots condicionales hermanos ({step===1 && …}{step===2 && …}),
	// así que React desmonta uno y monta el otro al avanzar. Si el hook viviera
	// dentro del paso, ese cambio lo remontaría: segundo POST /assign, y el
	// banner desaparecería y reaparecería —empujando el formulario— mientras
	// resuelve. Peor: si ese segundo assign fallara, la promesa se esfumaría en
	// la pantalla de credenciales, que es donde más importa.
	//
	// LinkingFlow no se desmonta al cambiar de paso, así que aquí el hook es
	// estable y los pasos sólo reciben un componente PURO ya resuelto.
	//
	// OJO: este flujo NO es el único. Onboarding v3 tiene sus propias pantallas
	// de vinculación y es por donde entra todo usuario nuevo; ahí el reparto es
	// host → ctx → pantalla (ver LinkingRewardPromise).
	const { rewardAmount, shouldPromise, onExplainerOpen } = useLinkingReward();
	const rewardBannerFor = (bleed: string) =>
		shouldPromise ? (
			<LinkingRewardBanner
				bleed={bleed}
				rewardAmount={rewardAmount}
				onExplainerOpen={onExplainerOpen}
			/>
		) : null;

	if (isLinked) {
		return (
			<SATAlreadyLinkedSuccess
				onContinue={
					onAlreadyLinkedContinue ?? (() => window.location.reload())
				}
			/>
		);
	}

	if (isPolling) {
		return <LinkingFlowStep3 handleCancelLinking={handleCancel} />;
	}

	// OJO: `variant='light'` no se pasa desde ningún lado del repo (verificado por
	// grep). Se mantiene por si vuelve, pero hoy esta rama es inalcanzable — no la
	// tomes como referencia de cómo se ve el banner en producción.
	if (variant === 'light') {
		return (
			<LinkingCredentialsLight
				header={
					<>
						{header}
						{/* Sin bleed: el header de esta variante no tiene padding que
						    cancelar, la franja ya nace a lo ancho. */}
						{rewardBannerFor('')}
					</>
				}
				onSubmit={handleSubmit}
				isLoading={isLoading}
			/>
		);
	}

	// El banner entra DENTRO de la tarjeta de cada paso, no colgado del <div>
	// raíz: aquí arriba abarcaría todo el viewport mientras la tarjeta está
	// centrada con max-width, y se leería como una barra pegada encima. Cada paso
	// recibe el bleed de SU padding, que no coincide entre ellos.
	return (
		<div>
			{step === 1 && (
				<LinkingFlowStep1
					rewardBanner={rewardBannerFor(BLEED.legacyStep1)}
					handleClickOption={handleSelectMethod}
					isOnboarding={isOnboarding}
					efirmaOnly={efirmaOnly}
					relink={relink}
					origin={origin}
				/>
			)}
			{step === 2 && (
				<LinkingFlowStep2
					rewardBanner={rewardBannerFor(BLEED.legacyStep2)}
					showModalFailed={showModalFailed}
					setShowModalFailed={setShowModalFailed}
					linkingOptionSelected={linkingOptionSelected}
					setLinkingOptionSelected={setLinkingOptionSelected}
					setStep={setStep}
					onSubmit={handleSubmit}
					isLoading={isLoading}
					directEntry={directEntry}
				/>
			)}
		</div>
	);
};

/**
 * Compuerta del experimento de entrada.
 *
 * El paso inicial se decide UNA vez, en el `useState` del orquestador, así que
 * los pasos no pueden montar antes de saber el brazo: si montaran, el control
 * pintaría el selector y el tratamiento saltaría al paso 2 un frame después,
 * con su parpadeo y su `linking_step_1_viewed` de más. Por eso el flujo real
 * vive en un hijo que sólo existe con el brazo ya resuelto.
 *
 * En la práctica resuelve en el primer efecto: las features se piden al boot y
 * `user_id` ya aterrizó en `Protected.routes` antes de que ninguna superficie de
 * vinculación sea alcanzable. El timeout del hook es la válvula para el caso
 * degradado, y cae a control.
 */
const LinkingFlow = (props: LinkingFlowProps) => {
	// Fuera del experimento las superficies que ya vienen comprometidas con un
	// método (`startMethod`, `efirmaOnly`) y la variante `light`: ahí no hay
	// selector que saltarse, así que meterlas sólo diluiría el denominador.
	const { resolved, directMethod } = useLinkingExperiments(
		props.variant !== 'light' &&
			!props.startMethod &&
			!props.efirmaOnly &&
			!props.relink,
	);

	if (!resolved) return null;

	// El `origin` se reparte por contexto y no por props: los nueve emisores de
	// `useLinkingTracking` cuelgan de aquí en cuatro niveles distintos (pasos,
	// drawers de ayuda, experiencia de espera, orquestador) y ninguno de los
	// componentes intermedios lo usa. Ver `linkingOriginContext`.
	const steps = (
		<LinkingFlowSteps
			{...props}
			startMethod={props.startMethod ?? directMethod ?? undefined}
			directEntry={directMethod !== null}
		/>
	);

	return (
		<LinkingOriginContext.Provider value={props.origin}>
			{props.relink ? <SatRelinkScope>{steps}</SatRelinkScope> : steps}
		</LinkingOriginContext.Provider>
	);
};

export default LinkingFlow;
